/**
 * utils/exportUtils.js
 *
 * Centralised export helpers for Year End Accounting:
 *   - downloadExcelTemplate(heads, type, fyName)  → .xlsx template with head columns
 *   - parseExcelImport(file)                       → array of row objects
 *   - downloadLedgerPDF(txns, type, fyName)        → .pdf of full ledger
 *   - downloadLedgerCSV(txns, type)                → .csv of full ledger
 *   - downloadRPPDF(report, fyName, societyName)   → R&P PDF
 *   - downloadRPExcel(report, fyName, societyName) → R&P Excel (.xlsx)
 *   - downloadPrintAllPDF(...)                     → Combined: Cash + Banks + Summary + R&P
 */

const FY_MONTHS = [
  'April','May','June','July','August','September',
  'October','November','December','January','February','March',
];

const monthFromDate = (d) => {
  const jsMonth = d.getMonth();
  const fyIndex = jsMonth >= 3 ? jsMonth - 3 : jsMonth + 9;
  return FY_MONTHS[fyIndex];
};

// ─── Excel Template ───────────────────────────────────────────────────────────
export const downloadExcelTemplate = async (heads, type, fyName) => {
  const XLSX  = await import('xlsx');
  const label = type === 'income' ? 'Income' : 'Expense';
  const refNo = type === 'income' ? 'Receipt No' : 'Voucher No (leave blank)';

  const fixedHeaders = [
    'Date (DD-MM-YYYY)',
    'Member Name',
    refNo,
    'Narration',
    'Cash Amount (Rs.)',
    'Bank Amount (Rs.)',
    'Bank Name (if Bank)',
  ];

  const headHeaders = heads.map(h => h.name);
  const headerRow   = [...fixedHeaders, ...headHeaders];

  const instructionRow = [
    '⚠ DD-MM-YYYY',
    'Optional',
    type === 'income' ? 'Your receipt no' : 'Leave blank',
    'Optional',
    '⚠ Cash portion (0 if none)',
    '⚠ Bank portion (0 if none)',
    '⚠ Only if Bank Amount > 0',
    ...heads.map(() => '⚠ Amount for this head (0 if none)'),
  ];

  const sample1Fixed = [
    '01-04-2024', 'Sample Member',
    type === 'income' ? 'RCP-001' : '',
    'Cash payment example', '5000', '0', '',
  ];
  const sample1 = [...sample1Fixed, ...heads.map((_, i) => i === 0 ? '5000' : '')];

  const sample2Fixed = [
    '05-04-2024', 'Another Member',
    type === 'income' ? 'RCP-002' : '',
    'Bank transfer example', '0', '3000', '(your bank name)',
  ];
  const sample2 = [...sample2Fixed, ...heads.map((_, i) => i === 0 ? '3000' : '')];

  const dataRows = [headerRow, instructionRow, sample1, sample2];

  if (heads.length >= 2) {
    const sample3Fixed = [
      '10-04-2024', 'Split Example',
      type === 'income' ? 'RCP-003' : '',
      'Two heads + cash/bank split', '2000', '3000', '(your bank name)',
    ];
    dataRows.push([...sample3Fixed, ...heads.map((_, i) => i === 0 ? '2000' : i === 1 ? '3000' : '')]);
  }

  const ws = XLSX.utils.aoa_to_sheet(dataRows);
  ws['!cols'] = [22, 18, 26, 22, 20, 20, 22, ...heads.map(() => ({ wch: 20 }))].map((v) => typeof v === 'number' ? { wch: v } : v);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `${label} Template`);

  const refRows = [
    ['Fill Rules', ''],
    ['• Each head has its own column — fill the amount for that head', ''],
    ['• Leave a head column blank (or 0) if not applicable for that row', ''],
    ['• Cash Amount + Bank Amount must equal the sum of all head columns', ''],
    ['• If Bank Amount > 0, Bank Name is required', ''],
    ['• Month is auto-derived from Date — no need to fill it', ''],
    ['• Date must be DD-MM-YYYY format', ''],
    ['', ''],
    ['Account Heads in this template', ''],
    ...heads.map((h, i) => [`${i + 1}. ${h.name}`, '']),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(refRows), 'Reference');
  XLSX.writeFile(wb, `${fyName}_${label}_Template.xlsx`);
};

// ─── Parse Excel Import ───────────────────────────────────────────────────────
export const parseExcelImport = async (file, heads, banks) => {
  const XLSX = await import('xlsx');

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'binary' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });

        const results = [];
        const errors  = [];

        const headLookup = {};
        heads.forEach(h => { headLookup[h.name.toLowerCase()] = h; });

        rows.forEach((row, idx) => {
          const dateVal = row['Date (DD-MM-YYYY)'];
          if (!dateVal || String(dateVal).startsWith('⚠') || String(dateVal).trim() === '') return;

          const rowNum = idx + 2;

          let dateObj;
          if (typeof dateVal === 'number') {
            const utc = new Date(Math.floor(dateVal - 25569) * 86400 * 1000);
            dateObj = new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
          } else if (dateVal instanceof Date) {
            dateObj = new Date(dateVal.getUTCFullYear(), dateVal.getUTCMonth(), dateVal.getUTCDate());
          } else {
            const parts = String(dateVal).trim().split('-');
            if (parts.length === 3) {
              const MONTH_ABBR = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
              const mNameIdx  = MONTH_ABBR.indexOf(parts[1].toLowerCase());
              if (mNameIdx >= 0) {
                dateObj = new Date(parseInt(parts[2], 10), mNameIdx, parseInt(parts[0], 10));
              } else {
                dateObj = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              }
            }
          }
          if (!dateObj || isNaN(dateObj)) {
            errors.push(`Row ${rowNum}: Invalid date "${dateVal}" — use DD-MM-YYYY format`);
            return;
          }

          const month      = monthFromDate(dateObj);
          const cashAmt    = Number(row['Cash Amount (Rs.)'] || row['Cash Amount (₹)'] || 0);
          const bankAmt    = Number(row['Bank Amount (Rs.)'] || row['Bank Amount (₹)'] || 0);
          const bankName   = String(row['Bank Name (if Bank)'] || '').trim();
          const memberName = String(row['Member Name'] || '').trim();
          const receiptNo  = String(row['Receipt No']  || '').trim();
          const narration  = String(row['Narration']   || '').trim();

          const headEntries = [];
          let headTotal = 0;

          Object.keys(row).forEach(colName => {
            const matched = headLookup[colName.toLowerCase()];
            if (!matched) return;
            const amt = Number(row[colName] || 0);
            if (amt > 0) {
              headEntries.push({ headId: matched._id, headName: matched.name, amount: amt });
              headTotal += amt;
            }
          });

          if (headEntries.length === 0) {
            errors.push(`Row ${rowNum}: No head amounts found — fill at least one head column`);
            return;
          }

          const paymentTotal = cashAmt + bankAmt;
          if (paymentTotal <= 0) {
            errors.push(`Row ${rowNum}: Cash Amount and/or Bank Amount must total > 0`);
            return;
          }

          if (Math.abs(paymentTotal - headTotal) > 0.01) {
            errors.push(`Row ${rowNum}: Payment total (₹${paymentTotal}) does not match head total (₹${headTotal}) — they must be equal`);
            return;
          }

          const paymentSplits = [];
          if (cashAmt > 0) paymentSplits.push({ mode: 'cash', amount: cashAmt });
          if (bankAmt > 0) {
            if (!bankName) { errors.push(`Row ${rowNum}: Bank Name is required when Bank Amount > 0`); return; }
            const matchedBank = banks.find(b => b.bankName.toLowerCase() === bankName.toLowerCase());
            if (!matchedBank) { errors.push(`Row ${rowNum}: Bank "${bankName}" not found — check FY bank accounts`); return; }
            paymentSplits.push({ mode: 'bank', bankId: matchedBank._id, bankName: matchedBank.bankName, amount: bankAmt });
          }

          const localDateStr = [
            dateObj.getFullYear(),
            String(dateObj.getMonth() + 1).padStart(2, '0'),
            String(dateObj.getDate()).padStart(2, '0'),
          ].join('-');

          results.push({ date: localDateStr, month, memberName, receiptNo, narration, headEntries, paymentSplits, totalAmount: headTotal });
        });

        resolve({ rows: results, errors });
      } catch (err) {
        resolve({ rows: [], errors: [`Failed to parse file: ${err.message}`] });
      }
    };
    reader.readAsBinaryString(file);
  });
};

// ─── Shared PDF header builder ────────────────────────────────────────────────
const buildPDFHeader = (doc, title, subtitle, meta = []) => {
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(245, 166, 35);
  doc.rect(0, 0, pageW, 8, 'F');
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 8, pageW, doc.internal.pageSize.getHeight() - 8, 'F');

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 40, 60);
  doc.text(title, 14, 22);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 110, 130);
  doc.text(subtitle, 14, 29);

  const metaX = pageW - 14;
  let metaY   = 16;
  meta.forEach(line => {
    doc.setFontSize(8);
    doc.setTextColor(120, 130, 150);
    doc.text(line, metaX, metaY, { align: 'right' });
    metaY += 5;
  });

  doc.setDrawColor(230, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(14, 33, pageW - 14, 33);

  return 38;
};

// ─── Shared autoTable style preset ────────────────────────────────────────────
const tableStyles = (accentColor = [245, 166, 35]) => ({
  styles: {
    fontSize: 8.5,
    cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
    textColor: [40, 50, 70],
    lineColor: [220, 224, 235],
    lineWidth: 0.2,
    font: 'helvetica',
    overflow: 'linebreak',
  },
  headStyles: {
    fillColor: accentColor,
    textColor: [255, 255, 255],
    fontStyle: 'bold',
    fontSize: 8.5,
    halign: 'left',
  },
  alternateRowStyles: { fillColor: [248, 249, 252] },
  bodyStyles:         { fillColor: [255, 255, 255] },
});

const rAmt = (n) => Number(n || 0).toLocaleString('en-IN');

// ─── PDF Export — Ledger ──────────────────────────────────────────────────────
export const downloadLedgerPDF = async (txns, type, fyName, societyName) => {
  const { default: jsPDF }     = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const label     = type === 'income' ? 'Income' : 'Expense';
  const accentRGB = type === 'income' ? [34, 197, 94] : [220, 53, 53];
  const doc       = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW     = doc.internal.pageSize.getWidth();

  const totalAmount = txns.reduce((s, t) => s + t.totalAmount, 0);
  const totalCash   = txns.reduce((s, t) => s + (t.cashAmount || 0), 0);
  const totalBank   = txns.reduce((s, t) => s + (t.bankAmount || 0), 0);

  const r = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN')}`;

  const startY = buildPDFHeader(
    doc, societyName,
    `${label} Ledger  |  Financial Year: ${fyName}`,
    [
      `Generated: ${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`,
      `Total Entries: ${txns.length}`,
      `Total: ${r(totalAmount)}`,
    ]
  );

  autoTable(doc, {
    startY, margin: { left: 14, right: 14 }, tableWidth: 'auto',
    head: [['Date', 'Month', type === 'income' ? 'Receipt No' : 'Voucher No', 'Member', 'Account Heads', 'Cash (Rs.)', 'Bank (Rs.)', 'Total (Rs.)']],
    body: txns.map(t => [
      new Date(t.date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }),
      t.month,
      type === 'income' ? (t.receiptNo || '-') : (t.voucherNo || '-'),
      t.memberName || '-',
      t.headEntries.map(h => `${h.headName}: ${rAmt(h.amount)}`).join(', '),
      (t.cashAmount || 0) > 0 ? rAmt(t.cashAmount) : '-',
      (t.bankAmount || 0) > 0 ? rAmt(t.bankAmount) : '-',
      rAmt(t.totalAmount),
    ]),
    styles: { fontSize:8, cellPadding:{top:3.5,right:5,bottom:3.5,left:5}, textColor:[40,50,70], lineColor:[220,224,235], lineWidth:0.2, font:'helvetica', overflow:'linebreak', valign:'middle' },
    headStyles: { fillColor:accentRGB, textColor:[255,255,255], fontStyle:'bold', fontSize:8, halign:'center', cellPadding:{top:5,right:5,bottom:5,left:5} },
    alternateRowStyles: { fillColor:[248,250,252] },
    bodyStyles:         { fillColor:[255,255,255] },
    columnStyles: { 0:{cellWidth:26,halign:'left'}, 1:{cellWidth:22,halign:'center'}, 2:{cellWidth:28,halign:'center'}, 3:{cellWidth:30,halign:'left'}, 4:{cellWidth:75,halign:'left'}, 5:{cellWidth:28,halign:'right'}, 6:{cellWidth:28,halign:'right'}, 7:{cellWidth:32,halign:'right',fontStyle:'bold'} },
    didParseCell: (data) => {
      if (data.column.index === 7 && data.section === 'body') {
        data.cell.styles.textColor = accentRGB;
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  const boxW   = pageW - 28;
  doc.setFillColor(250, 251, 253);
  doc.setDrawColor(210, 215, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, finalY, boxW, 16, 2, 2, 'FD');
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 90, 110);
  doc.text(`Entries: ${txns.length}`, 20, finalY + 6);
  doc.text(`Cash: ${r(totalCash)}`, 20, finalY + 12);
  doc.text(`Bank: ${r(totalBank)}`, pageW / 2 - 20, finalY + 9, { align:'center' });
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...accentRGB);
  doc.text(`Grand Total: ${r(totalAmount)}`, 14 + boxW - 5, finalY + 9, { align:'right' });
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(160, 170, 185);
  doc.text(`Drusshti - ${societyName} - ${fyName}`, 14, doc.internal.pageSize.getHeight() - 5);
  doc.text('Page 1', pageW - 14, doc.internal.pageSize.getHeight() - 5, { align:'right' });

  doc.save(`${fyName}_${label}_Ledger.pdf`);
};

// ─── CSV Export ───────────────────────────────────────────────────────────────
export const downloadLedgerCSV = (txns, type, fyName) => {
  const label  = type === 'income' ? 'Income' : 'Expense';
  const header = ['Date', 'Month', type === 'income' ? 'Receipt No' : 'Voucher No', 'Member', 'Heads', 'Cash (₹)', 'Bank (₹)', 'Total (₹)'];
  const rows   = txns.map(t => [
    new Date(t.date).toLocaleDateString('en-IN'),
    t.month,
    type === 'income' ? (t.receiptNo || '') : (t.voucherNo || ''),
    t.memberName || '',
    t.headEntries.map(h => `${h.headName}:${h.amount}`).join(' | '),
    t.cashAmount || 0, t.bankAmount || 0, t.totalAmount,
  ]);
  const csv  = [header, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `${fyName}_${label}_Ledger.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ─── Receipt & Payment PDF ────────────────────────────────────────────────────
export const downloadRPPDF = async (report, fyName, societyName) => {
  const amt = (n) => Number(n || 0).toLocaleString('en-IN');

  const { default: jsPDF }     = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const M     = 12;

  doc.setFillColor(180, 120, 20);
  doc.rect(0, 0, pageW, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(30, 35, 45);
  doc.text((societyName || 'Society').toUpperCase(), pageW / 2, 10, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 90, 110);
  doc.text(`Receipt and Payment Account  |  Financial Year: ${fyName}`, pageW / 2, 16, { align: 'center' });
  doc.setDrawColor(220, 224, 235);
  doc.setLineWidth(0.3);
  doc.line(M, 19, pageW - M, 19);

  const startY = 23;
  const { receipts, payments } = report;
  const openingBanks = receipts.opening.banks || [];
  const closingBanks = payments.closingBanks  || [];
  const receiptLines = receipts.lines  || [];
  const paymentLines = payments.lines  || [];
  const openingTotal = (receipts.opening.cash || 0) + openingBanks.reduce((s, b) => s + b.amount, 0);
  const grandTotal   = openingTotal + (receipts.total || 0);

  const recRows = [
    [{ content:'Opening Balance', styles:{ fontStyle:'bold', textColor:[30,35,45] } }, ''],
    ['  Cash Balance', amt(receipts.opening.cash || 0)],
    ...openingBanks.map(b => [`  Bank — ${b.bankName}`, amt(b.amount)]),
    ['', ''],
    ...receiptLines.map(l => [l.headName, amt(l.amount)]),
  ];

  const payRows = [
    ...paymentLines.map(l => [l.headName, amt(l.amount)]),
    ['', ''],
    [{ content:'Closing Balance', styles:{ fontStyle:'bold', textColor:[30,35,45] } }, ''],
    ['  Cash Balance', amt(payments.closingCash || 0)],
    ...closingBanks.map(b => [`  Bank — ${b.bankName}`, amt(b.balance)]),
  ];

  const maxLen = Math.max(recRows.length, payRows.length);
  while (recRows.length < maxLen) recRows.push(['', '']);
  while (payRows.length < maxLen) payRows.push(['', '']);

  const tableBody = recRows.map((r, i) => {
    const p = payRows[i];
    return [r[0], r[1], p[0], p[1]];
  });

  tableBody.push([
    { content:'Total', styles:{ fontStyle:'bold', fillColor:[240,253,244], textColor:[21,128,61] } },
    { content:amt(grandTotal), styles:{ fontStyle:'bold', fillColor:[240,253,244], textColor:[21,128,61], halign:'right' } },
    { content:'Total', styles:{ fontStyle:'bold', fillColor:[254,242,242], textColor:[185,28,28] } },
    { content:amt(grandTotal), styles:{ fontStyle:'bold', fillColor:[254,242,242], textColor:[185,28,28], halign:'right' } },
  ]);

  autoTable(doc, {
    startY, margin: { left: M, right: M },
    head: [[
      { content:'Receipts (Dr)',  styles:{ halign:'center', fillColor:[34,197,94],  textColor:[255,255,255], fontStyle:'bold', fontSize:9 } },
      { content:'Amount (Rs.)',   styles:{ halign:'right',  fillColor:[34,197,94],  textColor:[255,255,255], fontStyle:'bold', fontSize:9 } },
      { content:'Payments (Cr)', styles:{ halign:'center', fillColor:[220,53,53],  textColor:[255,255,255], fontStyle:'bold', fontSize:9 } },
      { content:'Amount (Rs.)',   styles:{ halign:'right',  fillColor:[220,53,53],  textColor:[255,255,255], fontStyle:'bold', fontSize:9 } },
    ]],
    body: tableBody,
    styles: { fontSize:8, cellPadding:{top:3,bottom:3,left:5,right:5}, textColor:[40,50,70], lineColor:[220,224,235], lineWidth:0.15, font:'helvetica', overflow:'linebreak', valign:'middle' },
    headStyles: { fontSize:8.5, cellPadding:{top:5,bottom:5,left:5,right:5} },
    alternateRowStyles: { fillColor:[248,250,252] },
    bodyStyles:         { fillColor:[255,255,255] },
    columnStyles: {
      0: { cellWidth:72, halign:'left'  },
      1: { cellWidth:23, halign:'right' },
      2: { cellWidth:72, halign:'left'  },
      3: { cellWidth:23, halign:'right' },
    },
    didDrawPage: (data) => {
      const h    = doc.internal.pageSize.getHeight();
      const midX = M + 72 + 23 + 0.5;
      doc.setDrawColor(180, 185, 200);
      doc.setLineWidth(0.4);
      doc.line(midX, data.settings.startY || startY, midX, data.cursor.y);
      doc.setFontSize(7);
      doc.setTextColor(160, 170, 185);
      doc.text(`Drusshti - ${societyName} - ${fyName}`, M, h - 5);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, pageW - M, h - 5, { align:'right' });
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const row = data.row.raw;
        const allEmpty = [0,1,2,3].every(i => {
          const cell = row[i];
          return !cell || (typeof cell === 'string' && cell.trim() === '') || (cell?.content === '');
        });
        if (allEmpty) data.cell.styles.minCellHeight = 3;
      }
    },
  });

  doc.save(`${fyName}_Receipt_Payment.pdf`);
};

// ─── Receipt & Payment Excel ──────────────────────────────────────────────────
export const downloadRPExcel = async (report, fyName, societyName) => {
  const XLSX = await import('xlsx');

  const { receipts, payments } = report;
  const openingBanks = receipts.opening.banks || [];
  const closingBanks = payments.closingBanks  || [];
  const receiptLines = receipts.lines  || [];
  const paymentLines = payments.lines  || [];
  const openingTotal = (receipts.opening.cash || 0) + openingBanks.reduce((s, b) => s + b.amount, 0);
  const grandTotal   = openingTotal + (receipts.total || 0);

  const rows = [
    [(societyName || 'Society').toUpperCase(), '', '', ''],
    [`Receipt and Payment Account for the period ${fyName}`, '', '', ''],
    ['', '', '', ''],
    ['Receipts (Dr)', 'Amount (Rs.)', 'Payments (Cr)', 'Amount (Rs.)'],
    ['Opening Balance', '', '', ''],
    ['  Cash Balance', receipts.opening.cash || 0, '', ''],
    ...openingBanks.map(b => [`  Bank — ${b.bankName}`, b.amount || 0, '', '']),
    ['', '', '', ''],
  ];

  const maxLen = Math.max(receiptLines.length, paymentLines.length);
  for (let i = 0; i < maxLen; i++) {
    const r = receiptLines[i];
    const p = paymentLines[i];
    rows.push([
      r ? r.headName : '',
      r ? (r.amount || 0) : '',
      p ? p.headName : '',
      p ? (p.amount || 0) : '',
    ]);
  }

  rows.push(['', '', '', '']);
  rows.push(['', '', 'Closing Balance', '']);
  rows.push(['', '', '  Cash Balance', payments.closingCash || 0]);
  closingBanks.forEach(b => rows.push(['', '', `  Bank — ${b.bankName}`, b.balance || 0]));
  rows.push(['', '', '', '']);
  rows.push(['Total', grandTotal, 'Total', grandTotal]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 40 }, { wch: 18 }];
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Receipt & Payment');
  XLSX.writeFile(wb, `${fyName}_Receipt_Payment.xlsx`);
};

// ─── Summary PDF ──────────────────────────────────────────────────────────────
export const downloadSummaryPDF = async (summary, fyName, societyName) => {
  const { default: jsPDF }     = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc   = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  const startY = buildPDFHeader(
    doc, societyName,
    `Income & Expenditure Summary  |  Financial Year: ${fyName}`,
    [`Generated: ${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`]
  );

  autoTable(doc, {
    startY,
    head: [[{ content:'Income', colSpan:2, styles:{ halign:'center', fillColor:[34,197,94], textColor:[255,255,255] } }], ['Account Head','Amount (Rs.)']],
    body: [
      ...Object.entries(summary.incomeByHead).map(([k,v]) => [k, Number(v).toLocaleString('en-IN')]),
      [{ content:'Total Income (Rs.)', styles:{ fontStyle:'bold', fillColor:[240,253,244], textColor:[21,128,61] } },
       { content:`Rs. ${Number(summary.totalIncome).toLocaleString('en-IN')}`, styles:{ fontStyle:'bold', fillColor:[240,253,244], textColor:[21,128,61], halign:'right' } }],
    ],
    ...tableStyles([34, 197, 94]),
    columnStyles: { 0:{ cellWidth:110 }, 1:{ cellWidth:50, halign:'right' } },
    margin: { left:14, right:14 },
  });

  const afterIncomeY = doc.lastAutoTable.finalY + 8;

  autoTable(doc, {
    startY: afterIncomeY,
    head: [[{ content:'Expenditure', colSpan:2, styles:{ halign:'center', fillColor:[239,68,68], textColor:[255,255,255] } }], ['Account Head','Amount (Rs.)']],
    body: [
      ...Object.entries(summary.expenseByHead).map(([k,v]) => [k, Number(v).toLocaleString('en-IN')]),
      [{ content:'Total Expenditure', styles:{ fontStyle:'bold', fillColor:[254,242,242], textColor:[185,28,28] } },
       { content:`Rs. ${Number(summary.totalExpense).toLocaleString('en-IN')}`, styles:{ fontStyle:'bold', fillColor:[254,242,242], textColor:[185,28,28], halign:'right' } }],
    ],
    ...tableStyles([239, 68, 68]),
    columnStyles: { 0:{ cellWidth:110 }, 1:{ cellWidth:50, halign:'right' } },
    margin: { left:14, right:14 },
  });

  const finalY    = doc.lastAutoTable.finalY + 8;
  const surplus   = summary.surplus;
  const isPositive = surplus >= 0;
  doc.setFillColor(...(isPositive ? [240,253,244] : [254,242,242]));
  doc.setDrawColor(...(isPositive ? [34,197,94] : [239,68,68]));
  doc.setLineWidth(0.5);
  doc.roundedRect(14, finalY, pageW - 28, 16, 2, 2, 'FD');
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...(isPositive ? [21,128,61] : [185,28,28]));
  doc.text(isPositive ? 'Surplus' : 'Deficit', 20, finalY + 10);
  doc.text(`Rs. ${Math.abs(surplus).toLocaleString('en-IN')}`, pageW - 20, finalY + 10, { align:'right' });
  doc.setFontSize(7); doc.setTextColor(160, 170, 185);
  doc.text(`Drusshti — ${societyName} — ${fyName}`, 14, doc.internal.pageSize.getHeight() - 6);

  doc.save(`${fyName}_Summary.pdf`);
};

// ─── Cash/Bank Report PDF ─────────────────────────────────────────────────────
export const downloadCashBankPDF = async (reportData, reportType, fyName, societyName) => {
  const { default: jsPDF }     = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const label  = reportType === 'cash' ? 'Cash Report' : `${reportData.bankName || 'Bank'} Report`;
  const doc    = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const fmtRs  = (n) => 'Rs. ' + Number(n||0).toLocaleString('en-IN');

  const startY = buildPDFHeader(
    doc, societyName || 'Society',
    `${label}  |  Financial Year: ${fyName || 'FY'}`,
    [
      `Opening: Rs. ${Number(reportData.openingBalance||0).toLocaleString('en-IN')}`,
      `Closing: Rs. ${Number(reportData.closingBalance||0).toLocaleString('en-IN')}`,
    ]
  );

  const rows = (reportData.rows || []).map(row => [
    row.date ? new Date(row.date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '-',
    row.description || '-',
    row.receiptNo || row.voucherNo || '',
    (row.debit  || 0) > 0 ? Number(row.debit).toLocaleString('en-IN')  : '-',
    (row.credit || 0) > 0 ? Number(row.credit).toLocaleString('en-IN') : '-',
    Number(row.balance || 0).toLocaleString('en-IN'),
  ]);

  autoTable(doc, {
    startY, margin:{ left:14, right:14 },
    head: [['Date','Description','Ref No','Debit (Rs.)','Credit (Rs.)','Balance (Rs.)']],
    body: rows,
    styles: { fontSize:8, cellPadding:{top:3.5,right:4,bottom:3.5,left:4}, textColor:[40,50,70], lineColor:[220,224,235], lineWidth:0.2, font:'helvetica', overflow:'linebreak', valign:'middle' },
    headStyles: { fillColor:[245,166,35], textColor:[255,255,255], fontStyle:'bold', fontSize:8, halign:'center', cellPadding:{top:5,right:4,bottom:5,left:4} },
    alternateRowStyles: { fillColor:[248,250,252] },
    bodyStyles:         { fillColor:[255,255,255] },
    columnStyles: { 0:{cellWidth:24,halign:'center'}, 1:{cellWidth:72,halign:'left'}, 2:{cellWidth:20,halign:'center'}, 3:{cellWidth:22,halign:'right'}, 4:{cellWidth:22,halign:'right'}, 5:{cellWidth:22,halign:'right',fontStyle:'bold'} },
    didParseCell: (data) => {
      if (data.column.index === 5 && data.section === 'body') {
        const val = parseFloat(String(data.cell.raw || '').replace(/[^0-9.-]/g, ''));
        if (!isNaN(val) && val < 0)    data.cell.styles.textColor = [220,53,53];
        else if (data.row.index === 0) data.cell.styles.textColor = [245,166,35];
      }
    },
  });

  const finalY = doc.lastAutoTable.finalY + 5;
  doc.setFillColor(250, 251, 253); doc.setDrawColor(210, 215, 225); doc.setLineWidth(0.3);
  doc.roundedRect(14, finalY, pageW - 28, 14, 2, 2, 'FD');
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 90, 110);
  doc.text(`Opening Balance: ${fmtRs(reportData.openingBalance)}`, 20, finalY + 6);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(245, 166, 35);
  doc.text(`Closing Balance: ${fmtRs(reportData.closingBalance)}`, pageW - 18, finalY + 6, { align:'right' });
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(160, 170, 185);
  doc.text(`Drusshti - ${societyName} - ${fyName}`, 14, doc.internal.pageSize.getHeight() - 5);
  doc.text('Page 1', pageW - 14, doc.internal.pageSize.getHeight() - 5, { align:'right' });

  doc.save(`${fyName}_${label.replace(/ /g, '_')}.pdf`);
};

// ─── Cash/Bank Report Excel ───────────────────────────────────────────────────
export const downloadCashBankExcel = async (reportData, reportType, fyName, societyName) => {
  const XLSX  = await import('xlsx');
  const label = reportType === 'cash' ? 'Cash Report' : `${reportData.bankName || 'Bank'} Report`;

  const headerRows = [
    [(societyName || 'Society').toUpperCase()],
    [`${label}  |  Financial Year: ${fyName}`],
    [],
    ['Date', 'Description', 'Ref No', 'Debit (Rs.)', 'Credit (Rs.)', 'Balance (Rs.)'],
  ];

  const dataRows = (reportData.rows || []).map(row => [
    row.date ? new Date(row.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
    row.description || '-',
    row.receiptNo || row.voucherNo || '',
    (row.debit  || 0) > 0 ? Number(row.debit)  : '',
    (row.credit || 0) > 0 ? Number(row.credit) : '',
    Number(row.balance || 0),
  ]);

  const summaryRows = [
    [],
    ['Opening Balance', Number(reportData.openingBalance || 0)],
    ['Closing Balance', Number(reportData.closingBalance || 0)],
  ];

  const ws = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows, ...summaryRows]);
  ws['!cols'] = [{ wch: 18 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 18 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, label.substring(0, 31));
  XLSX.writeFile(wb, `${fyName}_${label.replace(/ /g, '_')}.xlsx`);
};

// ─── Print All — Combined Report ──────────────────────────────────────────────
// Order: Cash Report → each Bank Report → Summary → Receipt & Payment
// Opens a print-ready HTML window with page breaks between sections.
export const downloadPrintAllPDF = ({
  cashReport,
  bankReports,    // { [bankId]: bankReportObj }
  banks,          // fy.bankOpeningBalances array
  summaryReport,
  rpReport,
  fyName,
  societyName,
}) => {
  const fmt     = n  => Number(n  || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  const fmtDate = d  => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  const EXPENSE_COLOR        = '#E8820C';
  const EXPENSE_COLOR_LIGHT  = '#fff7ed';
  const EXPENSE_COLOR_BORDER = '#fed7aa';
  const EXPENSE_COLOR_TEXT   = '#92400e';

  // ── Running ledger table ──
  const buildLedgerTable = (rows) => {
    const trs = (rows || []).map((r, i) => {
      const isOpening = r.type === 'opening';
      return `
        <tr style="background:${isOpening ? '#fdf8f3' : i % 2 === 0 ? '#fff' : '#fafafa'}">
          <td style="padding:5px 8px;font-size:11px;white-space:nowrap;border-bottom:1px solid #f0f0f0;">${fmtDate(r.date)}</td>
          <td style="padding:5px 8px;font-size:11px;color:${isOpening ? '#b45309' : '#444'};border-bottom:1px solid #f0f0f0;">${r.description || '—'}</td>
          <td style="padding:5px 8px;font-size:11px;font-family:monospace;color:#888;border-bottom:1px solid #f0f0f0;">${r.receiptNo || r.voucherNo || ''}</td>
          <td style="padding:5px 8px;font-size:11px;text-align:right;color:#c0392b;border-bottom:1px solid #f0f0f0;">${r.debit  > 0 ? fmt(r.debit)  : '—'}</td>
          <td style="padding:5px 8px;font-size:11px;text-align:right;color:#27ae60;border-bottom:1px solid #f0f0f0;">${r.credit > 0 ? fmt(r.credit) : '—'}</td>
          <td style="padding:5px 8px;font-size:11px;text-align:right;font-weight:700;color:${(r.balance || 0) < 0 ? '#c0392b' : '#1a1a2e'};border-bottom:1px solid #f0f0f0;">${fmt(r.balance)}</td>
        </tr>`;
    }).join('');

    return `
      <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
        <thead>
          <tr style="background:#f5a623;">
            <th style="padding:7px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#fff;text-align:left;">Date</th>
            <th style="padding:7px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#fff;text-align:left;">Description</th>
            <th style="padding:7px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#fff;text-align:left;">Ref</th>
            <th style="padding:7px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#fff;text-align:right;">Debit (₹)</th>
            <th style="padding:7px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#fff;text-align:right;">Credit (₹)</th>
            <th style="padding:7px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#fff;text-align:right;">Balance (₹)</th>
          </tr>
        </thead>
        <tbody>${trs}</tbody>
      </table>`;
  };

  // ── Balance summary cards ──
  const buildBalanceCards = (cards) => `
    <div style="display:flex;gap:12px;margin-bottom:14px;flex-wrap:wrap;">
      ${cards.map(c => `
        <div style="flex:1;min-width:120px;background:#fdf8f3;border:1px solid #e8d5c4;border-radius:6px;padding:10px 14px;">
          <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:#6b5e54;margin-bottom:4px;">${c.label}</div>
          <div style="font-size:15px;font-weight:800;color:${c.color || '#1a1a2e'};">
            ${c.value !== null ? '₹' + fmt(c.value) : c.text || ''}
          </div>
        </div>`).join('')}
    </div>`;

  // ── Section page header ──
  const sectionHeader = (title, subtitle = '') => `
    <div style="margin-bottom:16px;">
      <div style="font-size:15px;font-weight:900;color:#1a1a2e;text-transform:uppercase;letter-spacing:0.06em;">${title}</div>
      ${subtitle ? `<div style="font-size:11px;color:#888;margin-top:3px;">${subtitle}</div>` : ''}
    </div>`;

  // ── SECTION 1: Cash Report ──
  const cashSection = cashReport ? `
    <div class="print-section">
      ${sectionHeader('Cash Report', `Financial Year: ${fyName}`)}
      ${buildBalanceCards([
        { label: 'Opening Balance', value: cashReport.openingBalance },
        { label: 'Closing Balance', value: cashReport.closingBalance, color: (cashReport.closingBalance || 0) < 0 ? '#c0392b' : '#27ae60' },
      ])}
      ${buildLedgerTable(cashReport.rows)}
    </div>` : '';

  // ── SECTION 2: Bank Reports ──
  const bankSections = (banks || []).map(b => {
    const rep = bankReports[b._id];
    if (!rep) return '';
    return `
      <div class="print-section">
        ${sectionHeader(`${rep.bankName} — Bank Report`, `Financial Year: ${fyName}`)}
        ${buildBalanceCards([
          { label: 'Opening Balance', value: rep.openingBalance },
          { label: 'Closing Balance', value: rep.closingBalance, color: (rep.closingBalance || 0) < 0 ? '#c0392b' : '#27ae60' },
        ])}
        ${buildLedgerTable(rep.rows)}
      </div>`;
  }).join('');

  // ── SECTION 3: Summary ──
  const buildSummaryHeadBlocks = (detailObj, sectionColor, isExpense) => {
    if (!detailObj || Object.keys(detailObj).length === 0)
      return `<div style="padding:16px;text-align:center;color:#999;font-size:12px;">No entries</div>`;
    const refLabel = isExpense ? 'Voucher No' : 'Receipt No';
    return Object.entries(detailObj).map(([head, data], hi) => {
      const txRows = (data.transactions || []).map((t, ti) => `
        <tr style="background:${ti % 2 === 0 ? '#fafafa' : '#fff'}">
          <td style="padding:4px 8px 4px 12px;font-size:10px;color:#555;border-bottom:1px solid #f0f0f0;white-space:nowrap;">${fmtDate(t.date)}</td>
          <td style="padding:4px 8px;font-size:10px;color:#333;border-bottom:1px solid #f0f0f0;">${t.narration || '—'}</td>
          <td style="padding:4px 8px;font-size:10px;color:#444;border-bottom:1px solid #f0f0f0;">${t.memberName || '—'}</td>
          <td style="padding:4px 8px;font-size:10px;color:#666;border-bottom:1px solid #f0f0f0;font-family:monospace;">${t.receiptNo || '—'}</td>
          <td style="padding:4px 8px;font-size:11px;text-align:right;color:${sectionColor};border-bottom:1px solid #f0f0f0;font-weight:600;">₹${fmt(t.amount)}</td>
        </tr>`).join('');

      return `
        <div style="margin-bottom:12px;">
          <div style="background:${isExpense ? EXPENSE_COLOR_LIGHT : '#d1fae5'};border:1px solid ${isExpense ? EXPENSE_COLOR_BORDER : '#a7f3d0'};border-radius:6px 6px 0 0;padding:8px 12px;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:700;font-size:12px;color:${isExpense ? EXPENSE_COLOR_TEXT : '#065f46'};">${hi + 1}. ${head}</span>
            <span style="font-weight:800;font-size:13px;color:${sectionColor};">₹${fmt(data.total)}</span>
          </div>
          <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-top:none;">
            <thead>
              <tr style="background:#f5f5f5;">
                <th style="padding:5px 8px 5px 12px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">Date</th>
                <th style="padding:5px 8px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">Narration</th>
                <th style="padding:5px 8px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">Member / Particulars</th>
                <th style="padding:5px 8px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">${refLabel}</th>
                <th style="padding:5px 8px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:right;">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>${txRows}</tbody>
            <tfoot>
              <tr style="background:#f9fafb;border-top:2px solid #ddd;">
                <td colspan="4" style="padding:6px 8px;font-weight:700;font-size:11px;">Sub-total — ${head}</td>
                <td style="padding:6px 8px;text-align:right;font-weight:700;font-size:11px;color:${sectionColor};">₹${fmt(data.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>`;
    }).join('');
  };

  const summarySection = summaryReport ? `
    <div class="print-section">
      ${sectionHeader('Summary Report', `Financial Year: ${fyName}`)}
      <div style="margin-bottom:20px;">
        <div style="background:#d1fae5;border:1px solid #a7f3d0;border-radius:4px;padding:8px 12px;font-weight:900;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;color:#065f46;margin-bottom:12px;">Income</div>
        ${buildSummaryHeadBlocks(summaryReport.incomeByHeadDetail, '#059669', false)}
        <div style="display:flex;justify-content:space-between;padding:9px 12px;font-weight:700;font-size:12px;background:#d1fae5;color:#065f46;border-radius:4px;margin-top:8px;">
          <span>Total Income</span><span>₹${fmt(summaryReport.totalIncome)}</span>
        </div>
      </div>
      <div>
        <div style="background:${EXPENSE_COLOR_LIGHT};border:1px solid ${EXPENSE_COLOR_BORDER};border-radius:4px;padding:8px 12px;font-weight:900;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;color:${EXPENSE_COLOR_TEXT};margin-bottom:12px;">Expense</div>
        ${buildSummaryHeadBlocks(summaryReport.expenseByHeadDetail, EXPENSE_COLOR, true)}
        <div style="display:flex;justify-content:space-between;padding:9px 12px;font-weight:700;font-size:12px;background:${EXPENSE_COLOR_LIGHT};color:${EXPENSE_COLOR_TEXT};border-radius:4px;margin-top:8px;">
          <span>Total Expense</span><span>₹${fmt(summaryReport.totalExpense)}</span>
        </div>
      </div>
    </div>` : '';

  // ── SECTION 4: Receipt & Payment ──
  const rpSection = rpReport ? (() => {
    const { receipts, payments, receiptSideTotal } = rpReport;
    const openingBanks = receipts.opening.banks || [];
    const closingBanks = payments.closingBanks  || [];
    const receiptLines = receipts.lines  || [];
    const paymentLines = payments.lines  || [];
    const maxRows = Math.max(receiptLines.length, paymentLines.length);

    const zipRows = Array.from({ length: maxRows }, (_, i) => {
      const r = receiptLines[i];
      const p = paymentLines[i];
      return `
        <tr>
          <td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f0f0f0;">${r?.headName || ''}</td>
          <td style="padding:5px 8px;font-size:11px;text-align:right;color:#27ae60;font-weight:500;border-bottom:1px solid #f0f0f0;">${r ? fmt(r.amount) : ''}</td>
          <td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f0f0f0;">${p?.headName || ''}</td>
          <td style="padding:5px 8px;font-size:11px;text-align:right;color:#c0392b;font-weight:500;border-bottom:1px solid #f0f0f0;">${p ? fmt(p.amount) : ''}</td>
        </tr>`;
    }).join('');

    return `
      <div class="print-section">
        ${sectionHeader('Receipt & Payment Account', `Financial Year: ${fyName}`)}
        <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
          <thead>
            <tr>
              <th style="padding:8px;font-size:10px;font-weight:700;background:#27ae60;color:#fff;text-align:left;text-transform:uppercase;letter-spacing:0.06em;">Particulars (Receipts Dr)</th>
              <th style="padding:8px;font-size:10px;font-weight:700;background:#27ae60;color:#fff;text-align:right;text-transform:uppercase;letter-spacing:0.06em;">Amount (₹)</th>
              <th style="padding:8px;font-size:10px;font-weight:700;background:#c0392b;color:#fff;text-align:left;text-transform:uppercase;letter-spacing:0.06em;">Particulars (Payments Cr)</th>
              <th style="padding:8px;font-size:10px;font-weight:700;background:#c0392b;color:#fff;text-align:right;text-transform:uppercase;letter-spacing:0.06em;">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr style="background:#fdf8f3;">
              <td style="padding:6px 8px;font-weight:700;font-size:11px;border-bottom:1px solid #f0f0f0;" colspan="2">Opening Balance</td>
              <td style="border-bottom:1px solid #f0f0f0;"></td><td style="border-bottom:1px solid #f0f0f0;"></td>
            </tr>
            <tr>
              <td style="padding:5px 8px 5px 20px;font-size:11px;color:#555;border-bottom:1px solid #f0f0f0;">Cash Balance</td>
              <td style="padding:5px 8px;font-size:11px;text-align:right;border-bottom:1px solid #f0f0f0;">${fmt(receipts.opening.cash)}</td>
              <td style="border-bottom:1px solid #f0f0f0;"></td><td style="border-bottom:1px solid #f0f0f0;"></td>
            </tr>
            ${openingBanks.map(b => `
              <tr>
                <td style="padding:5px 8px 5px 20px;font-size:11px;color:#555;border-bottom:1px solid #f0f0f0;">Bank — ${b.bankName}</td>
                <td style="padding:5px 8px;font-size:11px;text-align:right;border-bottom:1px solid #f0f0f0;">${fmt(b.amount)}</td>
                <td style="border-bottom:1px solid #f0f0f0;"></td><td style="border-bottom:1px solid #f0f0f0;"></td>
              </tr>`).join('')}
            <tr><td colspan="4" style="padding:4px;border-bottom:1px solid #e5e7eb;"></td></tr>
            ${zipRows}
            <tr><td colspan="4" style="padding:4px;border-bottom:1px solid #e5e7eb;"></td></tr>
            <tr style="background:#fdf8f3;">
              <td style="border-bottom:1px solid #f0f0f0;"></td><td style="border-bottom:1px solid #f0f0f0;"></td>
              <td style="padding:6px 8px;font-weight:700;font-size:11px;border-bottom:1px solid #f0f0f0;" colspan="2">Closing Balance</td>
            </tr>
            <tr>
              <td style="border-bottom:1px solid #f0f0f0;"></td><td style="border-bottom:1px solid #f0f0f0;"></td>
              <td style="padding:5px 8px 5px 20px;font-size:11px;color:#555;border-bottom:1px solid #f0f0f0;">Cash Balance</td>
              <td style="padding:5px 8px;font-size:11px;text-align:right;font-weight:700;color:#b45309;border-bottom:1px solid #f0f0f0;">${fmt(payments.closingCash)}</td>
            </tr>
            ${closingBanks.map(b => `
              <tr>
                <td style="border-bottom:1px solid #f0f0f0;"></td><td style="border-bottom:1px solid #f0f0f0;"></td>
                <td style="padding:5px 8px 5px 20px;font-size:11px;color:#555;border-bottom:1px solid #f0f0f0;">Bank — ${b.bankName}</td>
                <td style="padding:5px 8px;font-size:11px;text-align:right;color:${(b.balance || 0) < 0 ? '#c0392b' : '#1a1a2e'};border-bottom:1px solid #f0f0f0;">${fmt(b.balance)}</td>
              </tr>`).join('')}
          </tbody>
          <tfoot>
            <tr style="background:#fdf8f3;font-weight:800;font-size:12px;">
              <td style="padding:8px;">Total</td>
              <td style="padding:8px;text-align:right;color:#b45309;">${fmt(receiptSideTotal)}</td>
              <td style="padding:8px;">Total</td>
              <td style="padding:8px;text-align:right;color:#b45309;">${fmt(receiptSideTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>`;
  })() : '';

  // ── Assemble full HTML ──
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>${societyName} — Full Report — ${fyName}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:Arial,Helvetica,sans-serif; font-size:12px; color:#1a1a2e; background:#fff; padding:20px; }
    .doc-header { text-align:center; padding-bottom:14px; border-bottom:3px solid #f5a623; margin-bottom:24px; }
    .doc-header h1 { font-size:20px; font-weight:900; color:#0F1A2E; text-transform:uppercase; letter-spacing:0.06em; }
    .doc-header p  { font-size:11px; color:#777; margin-top:4px; }
    .print-section { page-break-before:always; padding-top:8px; margin-bottom:32px; }
    .print-section:first-of-type { page-break-before:avoid; }
    .footer { text-align:center; font-size:9px; color:#aaa; border-top:1px solid #eee; padding-top:10px; margin-top:24px; }
    @media print {
      body { padding:8px; }
      .print-section { page-break-before:always; }
    }
  </style>
</head>
<body>
  <div class="doc-header">
    <h1>${societyName || 'Society'}</h1>
    <p>Complete Financial Report &nbsp;|&nbsp; Financial Year: ${fyName}</p>
    <p style="margin-top:4px;font-size:10px;color:#999;">Cash &amp; Bank Reports &nbsp;·&nbsp; Summary &nbsp;·&nbsp; Receipt &amp; Payment</p>
  </div>

  ${cashSection}
  ${bankSections}
  ${summarySection}
  ${rpSection}

  <div class="footer">System-generated report from Drusshti Housing Society Management &nbsp;|&nbsp; ${fyName}</div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=1100,height=900');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 800);
};
